/**
 * One slot. Three states. Every rule that reads them is in this file.
 *
 * The temptation with clothing is a wardrobe: tops, bottoms, shoes, a scent
 * layer, a cleanliness value. All of that is a simulation of getting dressed,
 * and none of it is a decision — because the game only ever asks one question
 * about what you are wearing, which is *whether you look like staff*. So there
 * is one slot, and the three things that can be in it are the three answers the
 * building has to that question:
 *
 *   UNIFORM   banquet blacks. Correct back of house, and the offence front of
 *             house: a server on the marble is somebody who has walked out of
 *             the part of the hotel they belong in, and that is the first rule
 *             the game teaches.
 *   SPARE     the second set in your locker. A uniform for every purpose the
 *             rules have — the *only* thing that tells it from the first is
 *             that it has not been anywhere, which is what takes a smell off
 *             you. It was already in the game as the joint counterplay; all
 *             that has happened is that it now has a name and a slot.
 *   CIVILIAN  what you turned up in. Nobody on the marble looks twice at you,
 *             because you read as a guest — and that is the whole trade,
 *             because you cannot clock in dressed like that and being back of
 *             house in them during a shift is its own offence.
 *
 * Which makes the slot a genuine three-way rather than a uniform toggle with a
 * spare taped to it: blacks are safe in the back and a crime in the front,
 * civvies are safe in the front and a crime in the back, and the spare is
 * blacks that cost you a trip to the locker to put on.
 *
 * Two states, not three, answer every rule — `isUniform` is the only predicate
 * anything outside this file is allowed to ask. The spare differing from the
 * uniform anywhere except the laundry is the bug this shape exists to prevent.
 *
 * Pure: it holds a string and knows the order of a cycle. It does not know what
 * a locker is, it does not know what a smell is, and it does not clear one —
 * the locker does that, because a module that both owns the state and reaches
 * into another system to tidy up after it is a module you cannot test.
 */

export const CLOTHING = Object.freeze({
  UNIFORM: 'uniform',
  SPARE: 'spare',
  CIVILIAN: 'civilian',
});

/**
 * The order the locker cycles in, and it is chosen rather than alphabetical.
 *
 * Uniform to spare is one press, which matters: the spare is the answer to
 * having smoked something, and the answer to a problem you have right now
 * should not be two presses away. Spare to civvies is the next press, so
 * getting scented twice in a shift costs you a lap through your own clothes —
 * you had one spare, and you have used it.
 */
export const CLOTHING_ORDER = Object.freeze([
  CLOTHING.UNIFORM,
  CLOTHING.SPARE,
  CLOTHING.CIVILIAN,
]);

/**
 * The one question the rules ask. The spare is a uniform; that is what makes it
 * a spare rather than a third outfit.
 */
export function isUniform(state) {
  return state === CLOTHING.UNIFORM || state === CLOTHING.SPARE;
}

/** What the prompt and the write-up call it. */
export function describeClothing(state) {
  switch (state) {
    case CLOTHING.SPARE:
      return 'your spare uniform';
    case CLOTHING.CIVILIAN:
      return 'your own clothes';
    default:
      return 'your uniform';
  }
}

export function createClothing({ start = CLOTHING.UNIFORM } = {}) {
  let state = CLOTHING_ORDER.includes(start) ? start : CLOTHING.UNIFORM;
  const listeners = new Set();

  function announce(from) {
    if (from === state) return;
    for (const listener of listeners) listener({ from, to: state });
  }

  return {
    get state() {
      return state;
    },
    /** The only predicate anything outside this file should be asking. */
    get isUniform() {
      return isUniform(state);
    },
    /**
     * Whether the clock will take a punch from you.
     *
     * Here rather than in the shift tracker on purpose. The shift tracker is
     * about time — when the window opens, whether you are late — and the clock
     * refusing you is not about time, it is about what you have got on.
     */
    get canClockIn() {
      return isUniform(state);
    },
    get label() {
      return describeClothing(state);
    },
    /** What the next press at the locker puts you in. */
    get next() {
      return CLOTHING_ORDER[(CLOTHING_ORDER.indexOf(state) + 1) % CLOTHING_ORDER.length];
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** One press at the locker: round the cycle. */
    change() {
      const from = state;
      state = this.next;
      announce(from);
      return { from, to: state };
    },

    /** Straight into a named state. The save and the creator use this. */
    wear(next) {
      if (!CLOTHING_ORDER.includes(next)) return { from: state, to: state };
      const from = state;
      state = next;
      announce(from);
      return { from, to: state };
    },

    toJSON: () => ({ state }),
    load(saved) {
      const from = state;
      state = CLOTHING_ORDER.includes(saved?.state) ? saved.state : CLOTHING.UNIFORM;
      announce(from);
    },
    clear() {
      const from = state;
      state = CLOTHING.UNIFORM;
      announce(from);
    },
  };
}
